import type { UseQueryResult } from '@tanstack/react-query';
import { useMemo } from 'react';

import { useAgentsAndTimeslotsQuery } from '@/features/appointments/api/getAppointmentSlots';
import type { AgentInfo } from '@/features/appointments/utils/AgentInfo';
import { getClubCode } from '@/lib/tagging/utils';
import useAppointmentStore, {
  isInBranchAppointment as isInBranchSelector,
  isOnlineAppointment as isOnlineSelector,
} from '@/store/AppointmentStore';
import useFallbackWorkTypeId from './useFallbackWorkTypeId';

export type ResolvedAgentsQueryResult = UseQueryResult<AgentInfo[], Error>;

const useResolvedAgentsQuery = (): ResolvedAgentsQueryResult => {
  const isInBranch = useAppointmentStore(isInBranchSelector);
  const isOnline = useAppointmentStore(isOnlineSelector);
  const selectedDestination = useAppointmentStore(
    (state) => state.selectedDestination,
  );
  const selectedBranch = useAppointmentStore((state) => state.selectedBranch);

  const clubCode = useMemo(() => getClubCode(), []);
  const fallbackWorkTypeId = useFallbackWorkTypeId();

  const onlinePrimaryQuery = useAgentsAndTimeslotsQuery(
    {
      workTypeId: selectedDestination.workTypeId,
      clubCode,
      inBranch: false,
    },
    {
      enabled: selectedDestination.workTypeId !== '' && isOnline,
    },
  );

  const inBranchPrimaryQuery = useAgentsAndTimeslotsQuery(
    {
      workTypeId: selectedDestination.workTypeId,
      inBranch: true,
      branchId: selectedBranch?.branchId ?? '',
    },
    {
      enabled:
        selectedDestination.workTypeId !== '' &&
        isInBranch &&
        !!selectedBranch?.branchId,
    },
  );

  const primaryQuery = isInBranch ? inBranchPrimaryQuery : onlinePrimaryQuery;

  const shouldUseFallback =
    primaryQuery.isSuccess && primaryQuery.data.length === 0;

  const onlineFallbackQuery = useAgentsAndTimeslotsQuery(
    {
      workTypeId: fallbackWorkTypeId,
      clubCode,
      inBranch: false,
    },
    {
      enabled: shouldUseFallback && isOnline && fallbackWorkTypeId !== '',
    },
  );

  const inBranchFallbackQuery = useAgentsAndTimeslotsQuery(
    {
      workTypeId: fallbackWorkTypeId,
      inBranch: true,
      branchId: selectedBranch?.branchId ?? '',
    },
    {
      enabled:
        shouldUseFallback &&
        isInBranch &&
        fallbackWorkTypeId !== '' &&
        !!selectedBranch?.branchId,
    },
  );

  const fallbackQuery = isInBranch
    ? inBranchFallbackQuery
    : onlineFallbackQuery;

  return shouldUseFallback ? fallbackQuery : primaryQuery;
};

export default useResolvedAgentsQuery;
